import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitElement } from 'lit';
import { store } from '../store.js';
import type { MessageKey } from '../i18n/messages.js';
import { pageStyles } from '../styles/page.js';
import { EXTERNAL } from '@healthgeek/utils';
import { iconHeart, iconSun, iconVolunteer, iconBriefcase } from '../illustrations.js';
import '@healthgeek/ui';

class HgPage extends LitElement {
  static styles = [pageStyles];

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    window.scrollTo({ top: 0 });
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  protected firstUpdated() {
    requestAnimationFrame(() => this.scrollHash());
  }

  protected scrollHash() {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const el = this.renderRoot.querySelector(`#${CSS.escape(id)}`);
    if (el instanceof HTMLElement) el.scrollIntoView({ block: 'start' });
  }

  protected unsubscribe?: () => void;
  protected t(key: MessageKey) {
    return store.t(key);
  }
}

const COURSES = [
  {
    title: 'Community Health Worker Foundations',
    category: 'Community',
    duration: '6 weeks',
    level: 'Intro',
    summary: 'Core competencies for CHWs: household visits, referral pathways, and culturally grounded care.',
  },
  {
    title: 'Clinic maintenance & device care',
    category: 'Technology',
    duration: '4 weeks',
    level: 'Intermediate',
    summary: 'Keep donated and refurbished equipment running in low-bandwidth clinic environments.',
  },
  {
    title: 'Health Literacy Facilitation',
    category: 'Education',
    duration: '3 weeks',
    level: 'Intro',
    summary: 'How to teach preventative care and chronic disease management in community settings.',
  },
];

@customElement('hg-learning-page')
export class HgLearningPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('nav.learning')}</div>
          <h1>${this.t('page.learning.title')}</h1>
          <p>${this.t('page.learning.lede')}</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap-wide">
          <div class="program-teaser">
            ${COURSES.map(
              (c) => html`
                <div class="teaser-card">
                  <div class="teaser-body">
                    <div class="teaser-tag">${c.category} · ${c.level}</div>
                    <h3>${c.title}</h3>
                    <p>${c.summary}</p>
                    <p class="muted" style="margin-top:12px">${c.duration}</p>
                  </div>
                </div>
              `,
            )}
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-involved-page')
export class HgInvolvedPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('help.section')}</div>
          <h1>${this.t('help.title')}</h1>
          <p>${this.t('help.lede')}</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          <ol class="ways">
            <li>
              <div class="chip forest">${iconHeart}</div>
              <div>
                <h3>${this.t('help.donate.title')}</h3>
                <p>${this.t('help.donate.body')}</p>
              </div>
            </li>
            <li>
              <div class="chip coral">${iconSun}</div>
              <div>
                <h3>${this.t('help.monthly.title')}</h3>
                <p>${this.t('help.monthly.body')}</p>
              </div>
            </li>
            <li>
              <div class="chip sky">${iconVolunteer}</div>
              <div>
                <h3>${this.t('help.geek.title')}</h3>
                <p>${this.t('help.geek.body')}</p>
              </div>
            </li>
            <li>
              <div class="chip amber">${iconBriefcase}</div>
              <div>
                <h3>${this.t('help.partner.title')}</h3>
                <p>${this.t('help.partner.body')}</p>
              </div>
            </li>
          </ol>
          <div class="actions">
            <hg-button href="/contact#donate">${this.t('cta.donate')}</hg-button>
            <hg-button href="/contact" variant="outlined">${this.t('nav.contact')}</hg-button>
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-donate-page')
export class HgDonatePage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('nav.donate')}</div>
          <h1>${this.t('page.donate.title')}</h1>
          <p>${this.t('help.donate.body')}</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          <form @submit=${this.onSubmit}>
            <div class="amounts">
              ${['$25', '$50', '$100', '$250'].map(
                (n) => html`<button type="button">${n}</button>`,
              )}
            </div>
            <div class="form-row">
              <div>
                <label>${this.t('contact.form.email')}</label>
                <input type="email" required placeholder="you@example.com" />
              </div>
              <div>
                <label>${this.t('contact.form.country')}</label>
                <input type="text" placeholder="Mali, United States, etc." />
              </div>
            </div>
            <hg-button type="submit">${this.t('cta.donate')}</hg-button>
            <p class="muted" style="margin-top:16px">${this.t('cta.fine1')} ${this.t('cta.fine2')}</p>
          </form>
        </div>
      </section>
    `;
  }

  private onSubmit = (event: Event) => {
    event.preventDefault();
  };
}

@customElement('hg-contact-page')
export class HgContactPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('contact.kicker')}</div>
          <h1>${this.t('contact.title')}</h1>
          <p>${this.t('contact.lede')}</p>
        </div>
      </section>

      <section class="section" id="donate">
        <div class="wrap">
          <div class="section-num">${this.t('contact.ways.section')}</div>
          <h2>${this.t('contact.ways.title')}</h2>
          <ol class="ways">
            <li>
              <div>
                <h3>${this.t('help.donate.title')}</h3>
                <p>${this.t('help.donate.body')}</p>
              </div>
            </li>
            <li>
              <div>
                <h3>${this.t('help.monthly.title')}</h3>
                <p>${this.t('help.monthly.body')}</p>
              </div>
            </li>
            <li>
              <div>
                <h3>${this.t('help.geek.title')}</h3>
                <p>${this.t('help.geek.body')}</p>
              </div>
            </li>
            <li>
              <div>
                <h3>${this.t('help.partner.title')}</h3>
                <p>${this.t('help.partner.body')}</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section class="section">
        <div class="wrap">
          <div class="section-num">${this.t('contact.form.section')}</div>
          <h2>${this.t('contact.form.title')}</h2>
          <form @submit=${(e: Event) => e.preventDefault()}>
            <div class="form-row">
              <div>
                <label>${this.t('contact.form.name')}</label>
                <input type="text" placeholder="Your full name" />
              </div>
              <div>
                <label>${this.t('contact.form.email')}</label>
                <input type="email" placeholder="you@example.com" />
              </div>
            </div>
            <div class="form-row">
              <div>
                <label>${this.t('contact.form.about')}</label>
                <select>
                  <option>${this.t('contact.form.opt.general')}</option>
                  <option>${this.t('contact.form.opt.donate')}</option>
                  <option>${this.t('contact.form.opt.volunteer')}</option>
                  <option>${this.t('contact.form.opt.partner')}</option>
                  <option>${this.t('contact.form.opt.juvo')}</option>
                  <option>${this.t('contact.form.opt.press')}</option>
                </select>
              </div>
              <div>
                <label>${this.t('contact.form.country')}</label>
                <input type="text" placeholder="Mali, United States, etc." />
              </div>
            </div>
            <div style="margin-bottom:20px">
              <label>${this.t('contact.form.message')}</label>
              <textarea placeholder="Tell us a little about what you're looking for."></textarea>
            </div>
            <hg-button type="submit">${this.t('contact.form.send')}</hg-button>
          </form>
        </div>
      </section>

      <section class="section no-border">
        <div class="wrap">
          <div class="section-num">${this.t('contact.direct.section')}</div>
          <div class="contact-block">
            <div class="n">${this.t('contact.email')}</div>
            <a href=${EXTERNAL.email}>contact@healthgeek.org.ml</a>
          </div>
          <div class="contact-block">
            <div class="n">${this.t('contact.mali')}</div>
            ${this.t('contact.mali.place')}
          </div>
          <div class="contact-block">
            <div class="n">${this.t('contact.us')}</div>
            ${this.t('contact.us.place')}
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-community-page')
export class HgCommunityPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('page.community.title')}</div>
          <h1>${this.t('help.geek.title')}</h1>
          <p class="first">${this.t('help.geek.body')}</p>
          <p>${this.t('gov.cac.body')}</p>
          <div class="actions">
            <hg-button href="/contact">${this.t('cta.geek')}</hg-button>
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-partners-page')
export class HgPartnersPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('page.partners.title')}</div>
          <h1>${this.t('help.partner.title')}</h1>
          <p>${this.t('help.partner.body')}</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          ${[
            'National Dental Board of Mali',
            'RobotsMali AI4D Lab',
            'Tribal Health Boards',
            'Indian Health Service',
            'Ministry of Health, Mali',
          ].map(
            (name) => html`
              <div class="contact-block"><strong>${name}</strong></div>
            `,
          )}
        </div>
      </section>
    `;
  }
}

@customElement('hg-news-page')
export class HgNewsPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('page.news.title')}</div>
          <h1>${this.t('page.news.title')}</h1>
          <p>Notes from the field — Bamako, Upper Michigan, and the clinics in between.</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          <div class="value-entry">
            <div class="n">MALI</div>
            <h3>Training cohort completes clinic-maintenance course in Bamako</h3>
            <p>September 2026 — Health workers certified on device care and referral tracking.</p>
          </div>
          <div class="value-entry">
            <div class="n">UNITED STATES</div>
            <h3>Upper Michigan partnership expands literacy workshops</h3>
            <p>August 2026 — Sessions co-designed with tribal health boards on navigating care systems.</p>
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-transparency-page')
export class HgTransparencyPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('page.transparency.title')}</div>
          <h1>${this.t('page.transparency.title')}</h1>
          <p>
            Annual filings and program spend summaries will be linked here as they are filed. Until then, write to
            contact@healthgeek.org.ml.
          </p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          <div class="contact-block">
            <div class="n">FORM 990</div>
            To be published after filing
          </div>
          <div class="contact-block">
            <div class="n">SAFEGUARDING</div>
            <a href="/legal/safeguarding">${this.t('page.safeguarding.title')}</a>
          </div>
          <div class="contact-block">
            <div class="n">PRIVACY</div>
            <a href="/legal/privacy">${this.t('page.privacy.title')}</a>
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-legal-page')
export class HgLegalPage extends HgPage {
  @property() kind: 'privacy' | 'terms' | 'safeguarding' = 'privacy';

  render() {
    const title =
      this.kind === 'privacy'
        ? this.t('page.privacy.title')
        : this.kind === 'terms'
          ? this.t('page.terms.title')
          : this.t('page.safeguarding.title');
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${title}</div>
          <h1>${title}</h1>
          <p>
            HealthGeek Initiative treats personal data, learner records, and community information with care. We collect
            only what we need to run programs, donations, and communications. We do not sell personal data.
          </p>
          <p>Safeguarding reports can be sent to contact@healthgeek.org.ml.</p>
        </div>
      </section>
    `;
  }
}

@customElement('hg-not-found-page')
export class HgNotFoundPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">404</div>
          <h1>${this.t('page.notfound.title')}</h1>
          <p>${this.t('page.notfound.body')}</p>
          <div class="actions">
            <hg-button href="/">${this.t('page.notfound.cta')}</hg-button>
          </div>
        </div>
      </section>
    `;
  }
}
